/**
 * Global Error Handler Service
 *
 * Provides centralized error handling for unhandled errors and promise rejections.
 * Integrates with the centralized logger and provides error recovery strategies.
 *
 * @module services/errorHandler
 */

import { logger } from './logger'

/**
 * Error severity levels for classification
 */
export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical'

/**
 * Error category for better organization and handling
 */
export type ErrorCategory = 'network' | 'storage' | 'validation' | 'runtime' | 'unknown'

/**
 * Structured error context for detailed error reporting
 */
export interface ErrorContext {
  category: ErrorCategory
  severity: ErrorSeverity
  userAction?: string
  componentStack?: string
  additionalData?: Record<string, unknown>
}

/**
 * Error handler configuration
 */
export interface ErrorHandlerConfig {
  /**
   * Enable/disable global error handling
   */
  enabled: boolean

  /**
   * Show user-friendly error notifications
   */
  showNotifications: boolean

  /**
   * Maximum number of errors to track in memory
   */
  maxErrorHistory: number

  /**
   * Custom error handler callback
   */
  onError?: (error: Error, context: ErrorContext) => void
}

/**
 * Error history entry for tracking
 */
export interface ErrorHistoryEntry {
  timestamp: Date
  error: Error
  context: ErrorContext
  userAgent: string
  url: string
}

/**
 * Default error handler configuration
 */
const DEFAULT_CONFIG: ErrorHandlerConfig = {
  enabled: true,
  showNotifications: !import.meta.env.PROD,
  maxErrorHistory: 50,
}

/**
 * Global Error Handler Class
 *
 * Handles uncaught errors and unhandled promise rejections throughout the application.
 * Provides error classification, logging, and recovery strategies.
 */
class GlobalErrorHandler {
  private config: ErrorHandlerConfig = DEFAULT_CONFIG
  private errorHistory: ErrorHistoryEntry[] = []
  private isInitialized = false

  /**
   * Initialize the global error handler
   * Sets up event listeners for uncaught errors and promise rejections
   *
   * @param config - Optional configuration to merge with defaults
   *
   * @example
   * errorHandler.initialize({ showNotifications: true })
   */
  initialize(config?: Partial<ErrorHandlerConfig>): void {
    if (this.isInitialized) {
      logger.warn('Error handler already initialized')
      return
    }

    if (config !== undefined) {
      this.config = { ...this.config, ...config }
    }

    if (!this.config.enabled) {
      logger.info('Error handler disabled')
      return
    }

    // Handle uncaught runtime errors
    window.addEventListener('error', this.handleError)

    // Handle unhandled promise rejections
    window.addEventListener('unhandledrejection', this.handlePromiseRejection)

    this.isInitialized = true
    logger.info('Global error handler initialized')
  }

  /**
   * Cleanup and remove event listeners
   * Call this when shutting down the application
   */
  destroy(): void {
    if (!this.isInitialized) {
      return
    }

    window.removeEventListener('error', this.handleError)
    window.removeEventListener('unhandledrejection', this.handlePromiseRejection)

    this.isInitialized = false
    logger.info('Global error handler destroyed')
  }

  /**
   * Handle uncaught runtime errors
   */
  private handleError = (event: ErrorEvent): void => {
    event.preventDefault() // Prevent default browser error handling

    const error = event.error instanceof Error ? event.error : new Error(event.message)
    const context = this.categorizeError(error, {
      userAction: 'Runtime error occurred',
      additionalData: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    })

    this.processError(error, context)
  }

  /**
   * Handle unhandled promise rejections
   */
  private handlePromiseRejection = (event: PromiseRejectionEvent): void => {
    event.preventDefault() // Prevent default browser error handling

    const error = event.reason instanceof Error ? event.reason : new Error(String(event.reason))

    const context = this.categorizeError(error, {
      userAction: 'Unhandled promise rejection',
    })

    this.processError(error, context)
  }

  /**
   * Categorize an error based on its type and message
   */
  private categorizeError(error: Error, baseContext: Partial<ErrorContext>): ErrorContext {
    let category: ErrorCategory = 'unknown'
    let severity: ErrorSeverity = 'medium'

    // Network errors
    if (
      error.name === 'NetworkError' ||
      error.message.toLowerCase().includes('network') ||
      error.message.toLowerCase().includes('fetch')
    ) {
      category = 'network'
      severity = 'medium'
    }
    // Storage errors
    else if (
      error.name === 'QuotaExceededError' ||
      error.message.toLowerCase().includes('storage') ||
      error.message.toLowerCase().includes('quota')
    ) {
      category = 'storage'
      severity = 'high'
    }
    // Validation errors
    else if (
      error.name === 'ValidationError' ||
      error.name === 'ZodError' ||
      error.message.toLowerCase().includes('validation')
    ) {
      category = 'validation'
      severity = 'low'
    }
    // Runtime errors
    else if (
      error.name === 'TypeError' ||
      error.name === 'ReferenceError' ||
      error.name === 'RangeError'
    ) {
      category = 'runtime'
      severity = 'high'
    }

    const context: ErrorContext = {
      category,
      severity,
    }

    if (baseContext.userAction !== undefined) {
      context.userAction = baseContext.userAction
    }

    if (baseContext.componentStack !== undefined) {
      context.componentStack = baseContext.componentStack
    }

    if (baseContext.additionalData !== undefined) {
      context.additionalData = baseContext.additionalData
    }

    return context
  }

  /**
   * Process an error with its context
   */
  private processError(error: Error, context: ErrorContext): void {
    // Log to centralized logger
    const logMessage = `[${context.category.toUpperCase()}] ${context.userAction ?? 'Error occurred'}`
    logger.error(logMessage, error)

    // Add to error history
    this.addToHistory(error, context)

    // Call custom error handler if provided
    this.config.onError?.(error, context)

    // Show user notification if enabled
    if (this.config.showNotifications) {
      this.showErrorNotification(error, context)
    }

    // Attempt recovery based on error type
    this.attemptRecovery(error, context)
  }

  /**
   * Add error to history with size limit
   */
  private addToHistory(error: Error, context: ErrorContext): void {
    const entry: ErrorHistoryEntry = {
      timestamp: new Date(),
      error,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href,
    }

    this.errorHistory.push(entry)

    // Maintain history size limit
    if (this.errorHistory.length > this.config.maxErrorHistory) {
      this.errorHistory.shift()
    }
  }

  /**
   * Show user-friendly error notification
   * In production, this could integrate with a toast/notification system
   */
  private showErrorNotification(error: Error, context: ErrorContext): void {
    const message = this.getUserFriendlyMessage(error, context)

    // In development, use console for now
    // In production, replace with your toast/notification system
    if (!import.meta.env.PROD) {
      console.group(`🚨 ${context.severity.toUpperCase()} Error`)
      console.error('Category:', context.category)
      console.error('Message:', message)
      console.error('Error:', error)
      if (context.additionalData !== undefined) {
        console.error('Additional Data:', context.additionalData)
      }
      console.groupEnd()
    }
  }

  /**
   * Generate user-friendly error message
   */
  private getUserFriendlyMessage(error: Error, context: ErrorContext): string {
    switch (context.category) {
      case 'network':
        return 'Network error occurred. Please check your internet connection and try again.'
      case 'storage':
        return 'Storage limit reached. Please clear some data or free up space.'
      case 'validation':
        return 'Invalid data detected. Please check your input and try again.'
      case 'runtime':
        return 'An unexpected error occurred. Please refresh the page and try again.'
      default:
        return 'Something went wrong. Please try again or contact support if the problem persists.'
    }
  }

  /**
   * Attempt to recover from errors
   */
  private attemptRecovery(error: Error, context: ErrorContext): void {
    switch (context.category) {
      case 'storage':
        logger.info('Attempting storage cleanup for recovery')
        // Could trigger cache cleanup or suggest user to clear data
        break
      case 'network':
        logger.info('Network error - consider retry logic')
        // Could trigger automatic retry with exponential backoff
        break
      default:
        // No automatic recovery for other error types
        break
    }
  }

  /**
   * Manually handle an error with custom context
   * Use this to explicitly handle errors in try/catch blocks with additional context
   *
   * @param error - Error to handle
   * @param context - Optional context to override automatic categorization
   *
   * @example
   * try {
   *   await saveData()
   * } catch (error) {
   *   errorHandler.handleManualError(error, {
   *     category: 'storage',
   *     severity: 'high',
   *     userAction: 'Saving nutrition data'
   *   })
   * }
   */
  handleManualError(error: unknown, context?: Partial<ErrorContext>): void {
    const err = error instanceof Error ? error : new Error(String(error))
    const fullContext =
      context !== undefined
        ? { ...this.categorizeError(err, context), ...context }
        : this.categorizeError(err, {})

    this.processError(err, fullContext)
  }

  /**
   * Get error history
   * Useful for debugging or sending to error tracking service
   *
   * @param count - Number of recent errors to retrieve (default: all)
   * @returns Array of recent error history entries
   */
  getErrorHistory(count?: number): ErrorHistoryEntry[] {
    if (count === undefined) {
      return [...this.errorHistory]
    }
    return this.errorHistory.slice(-count)
  }

  /**
   * Clear error history
   */
  clearHistory(): void {
    this.errorHistory = []
    logger.info('Error history cleared')
  }

  /**
   * Get error statistics
   * Useful for monitoring and analytics
   */
  getErrorStats(): {
    total: number
    byCategory: Record<ErrorCategory, number>
    bySeverity: Record<ErrorSeverity, number>
  } {
    const stats = {
      total: this.errorHistory.length,
      byCategory: {
        network: 0,
        storage: 0,
        validation: 0,
        runtime: 0,
        unknown: 0,
      } as Record<ErrorCategory, number>,
      bySeverity: {
        low: 0,
        medium: 0,
        high: 0,
        critical: 0,
      } as Record<ErrorSeverity, number>,
    }

    for (const entry of this.errorHistory) {
      stats.byCategory[entry.context.category]++
      stats.bySeverity[entry.context.severity]++
    }

    return stats
  }

  /**
   * Check if error handler is initialized
   */
  isActive(): boolean {
    return this.isInitialized
  }
}

/**
 * Singleton error handler instance
 * Use this throughout the application for consistent error handling
 *
 * @example
 * import { errorHandler } from '@/services/errorHandler'
 *
 * // Initialize in main.tsx
 * errorHandler.initialize()
 *
 * // Manual error handling
 * try {
 *   await saveData()
 * } catch (error) {
 *   errorHandler.handleManualError(error, {
 *     category: 'storage',
 *     severity: 'high',
 *     userAction: 'Saving data'
 *   })
 * }
 */
export const errorHandler = new GlobalErrorHandler()
